import { getPagesApi, getPostApi, getPostsApi } from '@/api-calls/posts/get'
import { createPostApi, updatePostApi } from '@/api-calls/posts/mutate'
import { trackDownloadApi } from '@/api-calls/unsplash/download'
import { suggestionItems } from '@/components/editor/slash-command'
import { ContentEditGuard } from '@/components/guards/content-edit-guard'
import Container from '@/components/header/container'
import {
  EditorCommand,
  EditorCommandEmpty,
  EditorCommandItem,
  EditorCommandList,
  EditorContent,
  type EditorInstance,
  EditorRoot,
  type JSONContent,
} from '@/components/tiptap-editor'
import {
  ImageModeToolbar,
  handleCommandNavigation,
} from '@/components/tiptap-editor/extensions'
import GenerativeMenuSwitch from '@/components/tiptap-editor/generative/generative-menu-switch'
import {
  handleImageDrop,
  handleImagePaste,
} from '@/components/tiptap-editor/plugins'
import { ColorSelector } from '@/components/tiptap-editor/selectors/color-selector'
import { LinkSelector } from '@/components/tiptap-editor/selectors/link-selector'
import { MathSelector } from '@/components/tiptap-editor/selectors/math-selector'
import { NodeSelector } from '@/components/tiptap-editor/selectors/node-selector'
import { TableMenu } from '@/components/tiptap-editor/selectors/table-menu'
import { TextButtons } from '@/components/tiptap-editor/selectors/text-buttons'
import { useImageForm } from '@/hooks/use-image-form'
import { createPostSchema, updatePostSchema } from '@/lib/schema/posts'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, PageTransition, Separator, SmoothLoader } from '@blogging/ui'
import { slugify } from '@blogging/utils'
import { useFetch, useMutation } from '@ngtrvu/use-api'
import { useQueryClient } from '@tanstack/react-query'
import { Trash } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { defaultExtensions } from './extensions'
import { uploadFn } from './image-upload'
import Panel from './panel'
import PostForm, { PostFormRef } from './post-form'
import { slashCommand } from './slash-command'
import { UnsplashForm, UnsplashImage } from './unsplash-form'

const TailwindAdvancedEditor = ({
  siteId,
  initialPostId,
  type,
}: {
  siteId: string
  initialPostId?: string
  type: 'post' | 'page'
}) => {
  const queryClient = useQueryClient()
  const postFormRef = useRef<PostFormRef>(null)

  const extensions = [...defaultExtensions, slashCommand]

  const [saveStatus, setSaveStatus] = useState('Saved')
  const [charsCount, setCharsCount] = useState(0)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [postId, setPostId] = useState<string | undefined>(initialPostId)
  const [saveNotification, setSaveNotification] = useState<string | null>(null)

  const [openNode, setOpenNode] = useState(false)
  const [openColor, setOpenColor] = useState(false)
  const [openLink, setOpenLink] = useState(false)
  const [openAI, setOpenAI] = useState(false)
  const [isEditorUnsplashOpen, setIsEditorUnsplashOpen] = useState(false)
  const unsplashEditorRef = useRef<EditorInstance | null>(null)
  const [postContent, setPostContent] = useState<JSONContent | undefined>()
  const [postHtml, setPostHtml] = useState<string | undefined>()
  const { handleDeleteImage, handleFileChange, isUploading } = useImageForm()

  const { mutate: trackDownload } = useMutation(trackDownloadApi)

  const { mutate: updatePost, isPending: isUpdatingContent } = useMutation(
    updatePostApi,
    {
      resourceName: 'post',
      onSuccess: (data: any) => {
        const { post } = data
        if (post) {
          queryClient.setQueryData(
            [getPostApi.apiName, { siteId, postId: post.id }],
            data,
          )
          setSaveNotification(
            post.status === 'published'
              ? 'Post saved & published!'
              : 'Post saved!',
          )
        }
      },
    },
  )
  const { mutate: createPost, isPending: isCreating } = useMutation(
    createPostApi,
    {
      resourceName: 'post',
      onSuccess: (data: any) => {
        const { post } = data
        if (post) {
          setPostId(post.id)
          setParams({ siteId, postId: post.id })
          queryClient.setQueryData(
            [getPostApi.apiName, { siteId, postId: post.id }],
            data,
          )
          invalidatePostsList()
          history.replaceState(null, '', `/${siteId}/editor/${type}/${post.id}`)
          setSaveNotification(
            post.status === 'published'
              ? 'Post saved & published!'
              : 'Post saved!',
          )
        }
      },
    },
  )
  const {
    data: post,
    isLoading: isLoadingPost,
    isError: isErrorPost,
    setParams,
  } = useFetch(getPostApi, {
    resourceName: 'post',
    autoLoad: !!initialPostId,
    initialParams: { siteId, postId: postId },
  })

  const formattedPost = post
    ? {
        ...post,
        publishedAt: post.publishedAt ? new Date(post.publishedAt) : undefined,
        excerpt: post.excerpt || '',
        tagIds: post.postsTags?.map((pt: any) => pt.tags.id) || [],
      }
    : undefined

  // Unified form state
  const form = useForm({
    resolver: zodResolver(postId ? updatePostSchema : createPostSchema),
    values: formattedPost,
    defaultValues: formattedPost || {
      title: '',
      slug: '',
      excerpt: '',
      publishedAt: undefined,
      visibility: 'public',
      featured: false,
      showTitleAndFeatureImage: true,
      status: 'draft',
      type: 'post',
      tagIds: [],
    },
  })

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen)

  // Keyboard shortcuts for save (Cmd+S) and publish (Cmd+P)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Cmd (Mac) or Ctrl (Windows/Linux)
      const isMod = e.metaKey || e.ctrlKey

      if (isMod && e.key === 's') {
        e.preventDefault()
        postFormRef.current?.save()
      }

      if (isMod && e.key === 'p') {
        e.preventDefault()
        postFormRef.current?.publish()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  useEffect(() => {
    if (saveNotification) {
      const timer = setTimeout(() => setSaveNotification(null), 3000)
      return () => clearTimeout(timer)
    }
  }, [saveNotification])

  // Opened by the "Unsplash" slash command, which hands over the editor
  // instance so the selected photo lands at the cursor.
  useEffect(() => {
    const handleOpenUnsplash = (event: Event) => {
      unsplashEditorRef.current =
        (event as CustomEvent<{ editor: EditorInstance }>).detail?.editor ??
        null
      setIsEditorUnsplashOpen(true)
    }
    window.addEventListener('editor:open-unsplash', handleOpenUnsplash)
    return () =>
      window.removeEventListener('editor:open-unsplash', handleOpenUnsplash)
  }, [])

  const handleUnsplashInsertToEditor = (image: UnsplashImage) => {
    const editor = unsplashEditorRef.current
    if (!editor || !image.urls.regular) return

    editor
      .chain()
      .focus()
      .setImage({
        src: image.urls.regular,
        alt:
          image.alt_description || `Photo by ${image.user.name} | Unsplash`,
      })
      .run()

    trackDownload({
      siteId,
      url: image.links.download_location,
    })
  }

  const handleUnsplashImageSelect = async (image: UnsplashImage) => {
    if (image.urls.regular) {
      const payload = {
        ...post,
        featureImage: image.urls.regular,
        featureImageAlt: `Photo by ${image.user.name} | Unsplash`,
        featureImageUnsplashCreditUrl: image.user.links.html,
      }
      queryClient.setQueryData(
        [getPostApi.apiName, { siteId, postId: postId }],
        { post: payload },
      )

      trackDownload({
        siteId,
        url: image.links.download_location,
      })
    }
  }

  // Handle feature image upload
  const handleFeatureImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const url = await handleFileChange(e)
    if (url) {
      const payload = {
        ...post,
        featureImage: url,
        featureImageUnsplashCreditUrl: null,
      }
      queryClient.setQueryData(
        [getPostApi.apiName, { siteId, postId: postId }],
        { post: payload },
      )
    }
  }

  const handleDeleteFeatureImage = () => {
    handleDeleteImage('image-upload')
    const payload = {
      ...post,
      featureImage: null,
      featureImageAlt: null,
      featureImageUnsplashCreditUrl: null,
    }
    queryClient.setQueryData([getPostApi.apiName, { siteId, postId: postId }], {
      post: payload,
    })
  }

  const onEditorUpdate = (editor: EditorInstance) => {
    const json = editor.getJSON()
    const html = editor.getHTML()
    setPostContent(json)
    setPostHtml(html)

    // update character count
    if (editor.storage && editor.storage.characterCount) {
      setCharsCount(editor.storage.characterCount.words())
    }
  }

  const invalidatePostsList = () => {
    if (type === 'post') {
      queryClient.invalidateQueries({
        queryKey: [getPostsApi.apiName, { siteId }],
      })
    } else {
      queryClient.invalidateQueries({
        queryKey: [getPagesApi.apiName, { siteId }],
      })
    }
  }

  const onSubmit = async (data: any) => {
    // Exclude non-schema fields that may be included from spreading post data
    const { postsTags, createdBy, site, createdAt, updatedAt, ...cleanData } =
      data

    const payload = {
      siteId,
      ...cleanData,
      featureImage: post?.featureImage, // Using post from query cache which might be updated by image handlers
      featureImageAlt: post?.featureImageAlt,
      featureImageUnsplashCreditUrl: post?.featureImageUnsplashCreditUrl,
      // Merge editor content
      json: postContent,
      html: postHtml,
    }

    // Include generated slug for new posts if not already present
    if (!postId && data.title && !data.slug) {
      payload.slug = slugify(data.title, false)
    }

    // Ensure publishedAt is ISO string if it's a Date
    if (payload.publishedAt instanceof Date) {
      payload.publishedAt = payload.publishedAt.toISOString()
    }

    if (postId) {
      try {
        await updatePost({
          id: postId,
          ...payload,
        })
      } catch (e) {
        console.error('Failed to save post', e)
        toast.error('Failed to save post')
      }
    } else {
      createPost({ ...payload, type })
    }
  }

  if (isLoadingPost) {
    return (
      <PageTransition
        isLoading={true}
        fallback={<SmoothLoader>Loading post...</SmoothLoader>}
      >
        Loading post...
      </PageTransition>
    )
  }

  if (isErrorPost) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-red-600">
            Error loading post
          </h3>
          <p className="text-sm text-gray-500 mt-2">
            Please try refreshing the page
          </p>
        </div>
      </div>
    )
  }

  if (postId && !post && !isLoadingPost && !isUpdatingContent && !isCreating) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h3 className="text-lg font-semibold">Post not found</h3>
          <p className="text-sm text-gray-500 mt-2">
            The post you're looking for doesn't exist
          </p>
        </div>
      </div>
    )
  }

  return (
    <ContentEditGuard
      contentOwnerId={post?.createdById}
      contentType={type}
      isLoading={isLoadingPost}
    >
      <div className="relative flex min-h-screen">
        {/* Main Editor Area */}
        <div className="flex-1 relative">
          <Container>
          <div className="relative z-0">
            {/* Article grid container - same layout as site */}
            <div className="py-10 sm:py-16 min-h-screen flex flex-col">
              <div className="article-grid">
                {/* Header/form section - content width */}
                <FormProvider {...form}>
                  <PostForm
                    ref={postFormRef}
                    onSubmit={onSubmit}
                    isSubmitting={isUpdatingContent || isCreating}
                    siteId={siteId}
                    isSidebarOpen={isSidebarOpen}
                    toggleSidebar={toggleSidebar}
                    saveNotification={saveNotification}
                  />
                </FormProvider>

                {/* Feature image - wide width */}
                {post?.featureImage ? (
                  <div className="my-6" data-width="wide">
                    <figure className="relative w-full">
                      <img
                        src={post?.featureImage}
                        alt="Feature image preview"
                        className="w-full rounded-lg"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="absolute top-4 left-4 h-8 w-8 p-0 rounded-md hover:bg-muted"
                        onClick={handleDeleteFeatureImage}
                        disabled={isUploading}
                        aria-label="Remove feature image"
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </figure>
                    <div className="text-sm text-muted-foreground mt-2">
                      <input
                        type="text"
                        className="w-full text-sm focus:outline-none focus:ring-0 rounded-md border border-border bg-background px-2 py-1"
                        value={post?.featureImageAlt || ''}
                        placeholder="Add alt text for the image..."
                        onChange={(e) => {
                          queryClient.setQueryData(
                            [getPostApi.apiName, { siteId, postId: postId }],
                            {
                              post: {
                                ...post,
                                featureImageAlt: e.target.value,
                              },
                            },
                          )
                        }}
                        aria-label="Feature image alt text"
                      />
                    </div>
                  </div>
                ) : (
                  <label
                    htmlFor="feature-image-upload"
                    className="flex flex-col w-full rounded-lg cursor-pointer"
                  >
                    <div className="flex flex-col items-start py-6">
                      <p className="mb-2 text-md text-gray-500 dark:text-gray-400 flex flex-row gap-4 items-center">
                        <span className="font-semibold">
                          {isUploading
                            ? 'Uploading...'
                            : 'Upload Feature Image'}
                        </span>
                        <UnsplashForm onSelect={handleUnsplashImageSelect} />
                      </p>
                    </div>
                    <input
                      id="feature-image-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFeatureImageUpload}
                      disabled={isUploading}
                    />
                  </label>
                )}
              </div>

              {/* Editor content - spans full grid width, creates its own internal grid */}
              <EditorRoot>
                <EditorContent
                  initialContent={post?.json as JSONContent}
                  extensions={extensions as any}
                  className="w-full min-h-[400px] bg-background"
                  editorProps={{
                    handleDOMEvents: {
                      keydown: (_view, event) => handleCommandNavigation(event),
                    },
                    handlePaste: (view, event) =>
                      handleImagePaste(view, event, uploadFn),
                    handleDrop: (view, event, _slice, moved) =>
                      handleImageDrop(view, event, moved, uploadFn),
                    attributes: {
                      class:
                        'article-grid prose prose-blogging dark:prose-invert prose-headings:font-title font-default focus:outline-none max-w-none',
                      'data-width': 'full',
                    },
                  }}
                  onUpdate={({ editor }) => {
                    onEditorUpdate(editor)
                    setSaveStatus('Unsaved')
                  }}
                  slotAfter={<ImageModeToolbar />}
                >
                  <EditorCommand className="z-50 h-auto max-h-[330px] overflow-y-auto bg-background px-1 py-2 transition-all">
                    <EditorCommandEmpty className="px-2 text-muted-foreground">
                      No results
                    </EditorCommandEmpty>
                    <EditorCommandList>
                      {suggestionItems.map((item) => (
                        <EditorCommandItem
                          value={item.title}
                          onCommand={(val) => item.command?.(val)}
                          className="flex w-full items-center space-x-2 rounded-md px-2 py-1 text-left text-sm hover:bg-accent aria-selected:bg-accent"
                          key={item.title}
                        >
                          <div className="flex h-10 w-10 items-center justify-center rounded-md border border-muted bg-background">
                            {item.icon}
                          </div>
                          <div>
                            <p className="font-medium">{item.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {item.description}
                            </p>
                          </div>
                        </EditorCommandItem>
                      ))}
                    </EditorCommandList>
                  </EditorCommand>

                  <GenerativeMenuSwitch open={openAI} onOpenChange={setOpenAI}>
                    <Separator orientation="vertical" />
                    <NodeSelector open={openNode} onOpenChange={setOpenNode} />
                    <Separator orientation="vertical" />
                    <LinkSelector open={openLink} onOpenChange={setOpenLink} />
                    <Separator orientation="vertical" />
                    <MathSelector />
                    <Separator orientation="vertical" />
                    <TextButtons />
                    <Separator orientation="vertical" />
                    <ColorSelector
                      open={openColor}
                      onOpenChange={setOpenColor}
                    />
                  </GenerativeMenuSwitch>

                  <TableMenu />
                </EditorContent>

                <div className="mt-auto sticky bottom-0 right-0 flex justify-end mb-5 gap-2 py-4 sm:py-8">
                  <div className="rounded-lg bg-accent px-2 py-1 text-sm text-muted-foreground">
                    {saveStatus}
                  </div>
                  <div
                    className={
                      charsCount
                        ? 'rounded-lg bg-accent px-2 py-1 text-sm text-muted-foreground'
                        : 'hidden'
                    }
                  >
                    {charsCount} Words
                  </div>
                </div>
              </EditorRoot>

              {/* Unsplash picker for the slash command (dialog only) */}
              <UnsplashForm
                open={isEditorUnsplashOpen}
                onOpenChange={setIsEditorUnsplashOpen}
                showTrigger={false}
                onSelect={handleUnsplashInsertToEditor}
              />
            </div>
          </div>
        </Container>
      </div>

      {/* Sidebar: responsive layout */}
      {isSidebarOpen && (
        <>
          {/* Backdrop (mobile only) */}
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm lg:hidden z-40"
            onClick={toggleSidebar}
            aria-label="Close sidebar"
          />

          {/* Sidebar panel */}
          <aside className="fixed lg:relative inset-y-0 right-0 z-40 lg:z-20 w-full sm:w-96 flex flex-col border-l bg-background shadow-lg lg:shadow-none transition-all duration-300 ease-in-out">
            <FormProvider {...form}>
              <Panel
                type={type}
                toggleSidebar={toggleSidebar}
                postId={postId}
                siteId={siteId}
                title={post?.title}
              />
            </FormProvider>
          </aside>
        </>
      )}
      </div>
    </ContentEditGuard>
  )
}

export default TailwindAdvancedEditor
